// Small timezone helpers for timezone-aware custom survey scheduling.
// No timezone library is installed (date-fns v2 only, no date-fns-tz), so
// anything that needs a *zone* uses the native Intl API instead - same as
// Nudges/ModalNudgeEditorButton already does for capturing the browser's
// own timezone. Plain local-time formatting still goes through date-fns.

import { format } from 'date-fns';

/**
 * The browser's own IANA timezone name, e.g. "America/New_York".
 */
export const getBrowserTimezone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * One IANA timezone as a SELECT option.
 *
 * All four keys are populated on purpose, because each stage of the SELECT
 * pipeline reads a different one: SelectInput's convertWithCallback rebuilds
 * each option as {...o, label: o.name, value: o.object}, myOptionFilter
 * matches on `name`/`label`, react-select itself reads `label`/`value`, and
 * SelectInput's remount key reads `name`. Omitting `object` in particular is
 * the documented way to break react-select's option matching - see
 * CreateCycleModal's toSourceOption comment.
 */

export interface TimezoneOption {
  name: string;
  object: string;
  label: string;
  value: string;
}

export const formatTimezoneName = (timeZone: string): string =>
  timeZone.replace(/_/g, ' ');

/**
 * The option object for one timezone name. Seeding a SELECT needs this
 * rather than a bare string: SelectInput hands props.value straight to
 * react-select as its `value`, so it has to be a whole option.
 */
export const getTimezoneOption = (timeZone: string): TimezoneOption => ({
  name: formatTimezoneName(timeZone),
  object: timeZone,
  label: formatTimezoneName(timeZone),
  value: timeZone,
});

/**
 * Every IANA timezone this browser knows, as SELECT options, built once at
 * module load.
 *
 * Exactly what Intl.supportedValuesOf returns is engine-dependent, so this
 * assumes very little about it: around 400 names, ascending, no duplicates -
 * but *not* necessarily canonicalized (this app's own Node build returns the
 * legacy Asia/Calcutta rather than Asia/Kolkata), so nothing here relies on a
 * place appearing under its modern name.
 *
 * supportedValuesOf needs Safari 15.4+; older engines fall back to just this
 * browser's own zone rather than rendering an empty picker.
 */
export const TIMEZONE_OPTIONS: TimezoneOption[] = (
  typeof Intl.supportedValuesOf === 'function'
    ? Intl.supportedValuesOf('timeZone')
    : [getBrowserTimezone()]
).map(getTimezoneOption);

/**
 * DatePicker (views/Widgets/Inputs/DatePicker.tsx) has no timezone concept -
 * it always displays/edits a Date's wall-clock components in the *browser's*
 * own timezone. To show a stored UTC instant "as it looks" in the survey's
 * own IANA timezone (which may differ from the viewer's), build a new local
 * Date whose own wall-clock components (as the browser would render them)
 * match that timezone's wall-clock components instead.
 *
 * This is display-only: it never touches the actual instant a Date
 * represents, only how it's presented to this picker. Submitting sends the
 * raw wall-clock digits plus a timezone name and lets the backend do the
 * authoritative conversion.
 */
export const getWallClockInTimeZone = (date: Date, timeZone: string): Date => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const get = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value);

  // Intl can format the "24:00" midnight-hour edge case as "24" - normalize
  // it to "0" the way the Date constructor expects.
  const hour = get('hour') % 24;

  return new Date(
    get('year'),
    get('month') - 1,
    get('day'),
    hour,
    get('minute'),
    get('second')
  );
};

/**
 * Inverse of getWallClockInTimeZone, for submission: reads a Date's own local
 * (browser) wall-clock components - exactly the digits the picker is
 * displaying - and formats them as a plain, zone-less ISO-like string. The
 * backend pairs that wall clock with scheduled_timezone and does the
 * authoritative UTC conversion (resolve_scheduled_time_to_utc); this
 * deliberately omits any offset rather than send a meaningless one.
 *
 * date-fns' format reads the same local getters, so the digits are the ones
 * on screen. 'T' has to stay quoted - v2 throws on an unescaped latin
 * character in a format string.
 */
export const formatWallClockAsNaiveIsoString = (date: Date): string =>
  format(date, "yyyy-MM-dd'T'HH:mm:ss");

/**
 * How far ahead of UTC `timeZone` is at a given instant, in milliseconds.
 * Derived by reading the zone's own wall clock at that instant and asking what
 * UTC time those digits would be, which is the offset by definition.
 */
const getZoneOffsetMs = (instantMs: number, timeZone: string): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date(instantMs));

  const get = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value);

  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour') % 24,
    get('minute'),
    get('second')
  );

  // Against the instant floored to the second, since the parts carry no
  // milliseconds and the remainder would otherwise show up as offset.
  return asUtc - Math.floor(instantMs / 1000) * 1000;
};

/**
 * The UTC instant a wall-clock reading denotes in a given timezone - the
 * inverse of getWallClockInTimeZone, for ordering rows that each carry their
 * own zone. Comparing the wall clocks alone would call noon in New York and
 * noon in London the same moment, when they are five hours apart.
 *
 * Resolved in two passes because a zone's offset depends on which instant you
 * ask about: the first pass can land on the far side of a DST boundary, and
 * the second re-reads the offset once it is in roughly the right place.
 */
export const getUtcInstantFromWallClock = (
  wallClock: Date,
  timeZone: string
): number => {
  const asIfUtc = Date.UTC(
    wallClock.getFullYear(),
    wallClock.getMonth(),
    wallClock.getDate(),
    wallClock.getHours(),
    wallClock.getMinutes(),
    wallClock.getSeconds()
  );

  const firstPass = asIfUtc - getZoneOffsetMs(asIfUtc, timeZone);
  return asIfUtc - getZoneOffsetMs(firstPass, timeZone);
};
